import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { dataQualityService } from '../../services/dataQualityService';
import DataQualityStatusBadge from '../DataQualityStatusBadge';
import DataQualityMetricWithImprovement from '../DataQualityMetricWithImprovement';
import DataQualityStackedBar from '../DataQualityStackedBar';
import {
  DEFAULT_DQ_TABLE,
  DQ_METRIC_KIND,
  ENRICH_TABLES,
  TABLE_LABELS,
  EMPTY_DISPLAY,
  formatFieldPairName
} from '../../constants/dataQuality';

/**
 * DEPRECATED — the pre-2026-07-10 Data Quality sections, kept visible on purpose.
 *
 * They are superseded by QualityOverview, EXCEPT for one thing the new view drops:
 * the before/after (raw ATS → cleaned) comparison. That comparison is the only place
 * you can see what the platform actually DID to the data, so it stays until the
 * reframed "created / rejected / extracted" view replaces it.
 *
 * Two known defects, deliberately left as-is rather than half-fixed:
 *
 *  1. The "Change" column mislabels a parse yield as a regression. For country/city the
 *     "before" is `company_location` — a raw address blob — not a country. So
 *     `country: 20.00% → 11.37%` reads "worsened" when it actually means "2,657
 *     companies had a location string and we extracted a country from 1,546 of them".
 *     Only like-for-like pairs (website, legal_name, name) read correctly.
 *
 *  2. The narrative box never renders. The Go service returns `narrative_json: {}`
 *     always; it was only ever populated by the retired Python profiler.
 *
 * Invalid% is structurally 0 everywhere (internal/dataquality metrics.go: "Phase 1
 * treats valid = present and invalid = 0"), so those columns are placeholders.
 */

function sortTablesByEnrichOrder(tables) {
  const order = new Map(ENRICH_TABLES.map((name, index) => [name, index]));
  return [...tables].sort((a, b) => (order.get(a.table_name) ?? 99) - (order.get(b.table_name) ?? 99));
}

function pickDefaultTable(tableList) {
  if (tableList.some((t) => t.table_name === DEFAULT_DQ_TABLE)) return DEFAULT_DQ_TABLE;
  return tableList[0]?.table_name || DEFAULT_DQ_TABLE;
}

export default function LegacyQualityPanel({ overview, tables, runId, onViewData }) {
  const [open, setOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(DEFAULT_DQ_TABLE);
  const [tableDetail, setTableDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const sortedTables = useMemo(() => sortTablesByEnrichOrder(tables || []), [tables]);
  const tableOptions = useMemo(() => {
    const names = sortedTables.length ? sortedTables.map((t) => t.table_name) : ENRICH_TABLES;
    return names.map((name) => ({ value: name, label: TABLE_LABELS[name] || name }));
  }, [sortedTables]);

  useEffect(() => {
    if (sortedTables.length) setSelectedTable(pickDefaultTable(sortedTables));
  }, [sortedTables]);

  const loadDetail = useCallback(async (table) => {
    if (!table) return;
    setDetailLoading(true);
    try {
      setTableDetail(await dataQualityService.getTableDetail(runId, table));
    } catch (_) {
      setTableDetail({ comparisons: [] });
    } finally {
      setDetailLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    if (open && selectedTable) loadDetail(selectedTable);
  }, [open, selectedTable, loadDetail]);

  return (
    <section className="bg-white rounded-xl shadow-sm border border-amber-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left px-6 py-4 flex items-center justify-between gap-4"
      >
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            Before &amp; after cleaning
            <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
              Deprecated
            </span>
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            The old Data Quality tables. Kept because they show what the platform changed — the one
            thing the new view above doesn&apos;t. Read the caveats before trusting the “Change” column.
          </p>
        </div>
        <span className="text-xs text-gray-500 flex-none">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-6 border-t border-gray-100 pt-5">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-900 space-y-1.5">
            <p className="font-semibold">Known defects — do not read these numbers naively:</p>
            <p>
              <strong>“Change” conflates three different things.</strong> For <code>country</code> and{' '}
              <code>city</code> the “before” is <code>company_location</code>, a raw address string —
              not a country. So <code>country 20.00% → 11.37%</code>, shown here as{' '}
              <strong>Declined</strong>, really means “2,657 companies had a location; we extracted a
              country from 1,546 of them” — a 58% parse yield, not lost data. Only like-for-like pairs
              read correctly: <code>legal_name</code> genuinely <em>created</em> 9,656 values the ATS
              never had, and <code>website</code> genuinely <em>rejected</em> 88 that failed validation.
            </p>
            <p>
              <strong>Invalid % is always 0.</strong> Nothing is format-checked yet — “valid” currently
              means “has a value”.
            </p>
            <p>
              <strong>The narrative box never renders.</strong> The Go profiler returns an empty
              narrative; only the retired Python profiler filled it.
            </p>
          </div>

          {overview ? (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Overall quality mix (after cleaning)</h4>
              <DataQualityStackedBar
                validPct={Number(overview.valid_pct || 0)}
                emptyPct={Number(overview.empty_pct || 0)}
                invalidPct={Number(overview.invalid_pct || 0)}
              />
            </div>
          ) : null}

          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h4 className="text-sm font-semibold text-gray-900">Table summary</h4>
              <p className="text-xs text-gray-500">
                Percentages are after cleaning. Coloured pp shows change vs before.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-4 font-medium">Table</th>
                    <th className="py-2 pr-4 font-medium">Rows</th>
                    <th className="py-2 pr-4 font-medium">Fields examined</th>
                    <th className="py-2 pr-4 font-medium">Valid %</th>
                    <th className="py-2 pr-4 font-medium">Missing %</th>
                    <th className="py-2 pr-4 font-medium">Invalid %</th>
                    <th className="py-2 pr-4 font-medium">Health</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTables.map((row) => (
                    <tr
                      key={row.table_name}
                      className={`border-b hover:bg-gray-50 cursor-pointer ${
                        selectedTable === row.table_name ? 'bg-teal-50/50' : ''
                      }`}
                      onClick={() => setSelectedTable(row.table_name)}
                    >
                      <td className="py-3 pr-4 font-medium text-gray-900">
                        {TABLE_LABELS[row.table_name] || row.table_name}
                      </td>
                      <td className="py-3 pr-4 tabular-nums">{Number(row.row_count || 0).toLocaleString()}</td>
                      <td className="py-3 pr-4 tabular-nums">
                        {row.fields_examined ?? row.profiled_field_count ?? EMPTY_DISPLAY}
                      </td>
                      <td className="py-3 pr-4">
                        <DataQualityMetricWithImprovement
                          pct={row.valid_pct ?? row.enrich_valid_pct}
                          improvementPct={row.valid_delta_pct ?? row.valid_improvement_pct ?? row.validity_delta_pct}
                          metricKind={DQ_METRIC_KIND.VALID}
                        />
                      </td>
                      <td className="py-3 pr-4">
                        <DataQualityMetricWithImprovement
                          pct={row.missing_pct}
                          improvementPct={row.missing_delta_pct ?? row.missing_improvement_pct}
                          metricKind={DQ_METRIC_KIND.MISSING}
                        />
                      </td>
                      <td className="py-3 pr-4">
                        <DataQualityMetricWithImprovement
                          pct={row.invalid_pct}
                          improvementPct={row.invalid_delta_pct ?? row.invalid_improvement_pct}
                          metricKind={DQ_METRIC_KIND.INVALID}
                        />
                      </td>
                      <td className="py-3 pr-4">
                        <DataQualityStatusBadge status={row.quality_status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">Table details</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Field-level before/after comparison for stage and clean columns.
                </p>
              </div>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-gray-600 font-medium text-xs">Select table</span>
                <select
                  value={selectedTable}
                  onChange={(e) => setSelectedTable(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 min-w-[180px] bg-white text-sm"
                >
                  {tableOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {detailLoading ? (
              <p className="text-sm text-gray-500">Loading field details…</p>
            ) : (
              <>
                {tableDetail?.table_profile ? (
                  <button
                    type="button"
                    onClick={() => onViewData(selectedTable, null)}
                    className="text-sm px-3 py-1.5 rounded-lg border border-[#00C6AE] text-[#007A6B] hover:bg-teal-50"
                  >
                    View sample improvements
                  </button>
                ) : null}

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2 pr-4 font-medium">Field</th>
                        <th className="py-2 pr-4 font-medium">Raw %</th>
                        <th className="py-2 pr-4 font-medium">Valid %</th>
                        <th className="py-2 pr-4 font-medium">Missing %</th>
                        <th className="py-2 pr-4 font-medium">Invalid %</th>
                        <th className="py-2 pr-4 font-medium">Change</th>
                        <th className="py-2 pr-4 font-medium">Examples</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(tableDetail?.comparisons || []).map((cmp) => (
                        <tr key={cmp.field_pair_name} className="border-b hover:bg-gray-50">
                          <td className="py-3 pr-4 font-medium text-gray-900">
                            {formatFieldPairName(cmp.field_pair_name)}
                          </td>
                          <td className="py-3 pr-4 tabular-nums text-gray-500">
                            {Number(cmp.stage_valid_pct || 0).toFixed(2)}%
                          </td>
                          <td className="py-3 pr-4">
                            <DataQualityMetricWithImprovement
                              pct={cmp.enrich_valid_pct}
                              improvementPct={cmp.valid_delta_pct}
                              metricKind={DQ_METRIC_KIND.VALID}
                            />
                          </td>
                          <td className="py-3 pr-4">
                            <DataQualityMetricWithImprovement
                              pct={cmp.enrich_empty_pct}
                              improvementPct={cmp.empty_delta_pct}
                              metricKind={DQ_METRIC_KIND.MISSING}
                            />
                          </td>
                          <td className="py-3 pr-4">
                            <DataQualityMetricWithImprovement
                              pct={cmp.enrich_invalid_pct}
                              improvementPct={cmp.invalid_delta_pct}
                              metricKind={DQ_METRIC_KIND.INVALID}
                            />
                          </td>
                          <td className="py-3 pr-4">
                            <DataQualityStatusBadge status={cmp.improvement_status} kind="improvement" />
                          </td>
                          <td className="py-3 pr-4">
                            <button
                              type="button"
                              onClick={() => onViewData(selectedTable, cmp.field_pair_name)}
                              className="text-sm text-[#007A6B] hover:underline"
                            >
                              View data
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {tableDetail && (tableDetail.comparisons || []).length === 0 ? (
                  <p className="text-sm text-gray-500">No stage/clean field pairs profiled for this table yet.</p>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
